import { createContext, useContext, type ReactNode } from "react";
import type { NavDevice } from "analytics";

import { useSidebarOptional } from "components/common/Sidebar/Sidebar";

export interface AppSidebarLayout {
  /** Icons only (60px). Never true inside the mobile sheet. */
  collapsed: boolean;
  device: NavDevice;
}

const DEFAULT_LAYOUT: AppSidebarLayout = { collapsed: false, device: "desktop" };

const AppSidebarLayoutContext = createContext<AppSidebarLayout | null>(null);

export const AppSidebarLayoutProvider = ({
  value,
  children,
}: {
  value: AppSidebarLayout;
  children: ReactNode;
}) => <AppSidebarLayoutContext.Provider value={value}>{children}</AppSidebarLayoutContext.Provider>;

/**
 * How the sidebar is currently laid out, for rows, flyouts and analytics.
 * Outside the sidebar (the header, a page) it reads the shell's sidebar
 * state, so clicks there report the same collapsed and device fields.
 */
export const useAppSidebarLayout = (): AppSidebarLayout => {
  const layout = useContext(AppSidebarLayoutContext);
  const shell = useSidebarOptional();
  if (layout) return layout;
  if (!shell) return DEFAULT_LAYOUT;
  return {
    collapsed: !shell.isMobile && shell.state === "collapsed",
    device: shell.isMobile ? "mobile" : "desktop",
  };
};
